import React, { useState, useCallback, useMemo, Fragment } from 'react';

import classnames from 'classnames';
import dynamic from 'next/dynamic';
import Overflow from 'rc-overflow';

import LinkButton from 'components/uiLibrary/LinkButton';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

import EntityName from 'components/Globals/EntityName';
import StickyContainer from 'components/Globals/Layout/StickyContainer';
import ParseHtml from 'components/ParseHtml';
import { DurationAndLangInfo } from 'components/Productions/Display/QuickView/renderers/ProductionComponents';
import { ProductionTags } from 'components/Productions/Display/Slugs/ProductionSlug';

import { TP, DATE_FORMATS } from 'constants/index';

import { createDate } from 'utils/date';
import useAppContext from 'utils/hooks/useAppContext';
import usePageContext from 'utils/hooks/usePageContext';
import { useProductionSummary, getProductionWorks, getProductionCredits } from 'utils/productions';
import { useQuery } from 'utils/react-query';

import queries from 'containers/Productions/queries';

import { i18n, useTranslation } from 'src/i18n';

import { FILTER_TYPES } from 'operabase-router/constants';

import ProductionCalendarDates from '../ProductionCalendarDates';
import { SECTION_REFS, SECTION_KEYS } from '../QuickView/constants';
import DateAndVenue from '../QuickView/DateAndVenue';
import LivestreamAndVod from '../QuickView/LivestreamAndVod';
import NavigationTabs from '../QuickView/NavigationTabs';
import ProductionActions from '../QuickView/ProductionActions';
import WorkTitle from '../QuickView/ProductionInfo/WorkTitle';
import { scrollToSection } from '../QuickView/utils';

import DateRangeAndPrefCount from './DateRangeAndPrefCount';
import classes from './ProductionStubInfo.module.scss';

const RentalBanner = dynamic(() => import('components/Productions/Display/RentalBanner'));

export const ProductionStubDateInfo = ({ production, showCalendarIcon, isProductionHeader }) => {
  const { t } = useTranslation('NS_DISPLAY_V4');
  const productionSummary = useProductionSummary(production);

  return (
    <div
      className={classnames(classes.datesInfo, {
        [classes.cancelled]: productionSummary?.isCancelled,
        [classes.headerInfo]: isProductionHeader,
      })}
    >
      {showCalendarIcon && <SpriteIcon icon="calendar" size={14} className={classes.icon} />}
      <Typography className={classes.date} weight="medium">
        {productionSummary?.dateRange?.dates} {productionSummary?.dateRange?.years}
      </Typography>
      {productionSummary?.performances?.length > 0 && (
        <Typography color="secondary" className={classes.date}>
          {' ('}
          {t(`${TP}.FN_PERFORMANCES_COUNT`, { count: productionSummary?.performances?.length })})
        </Typography>
      )}
    </div>
  );
};

export const LangAndSubTitles = ({ production, size = 14, showSlug = false }) => (
  <>
    {(production?.subtitles?.length > 0 || production?.languages?.length > 0) && (
      <>
        {production?.languages?.length > 0 && (
          <div className={classes.languages}>
            <SpriteIcon icon="world" size={size} className={classes.icon} />
            {production?.languages?.map((subtitle, langIndex) => (
              <Typography className={classes.language} size={size} color="secondary" key={subtitle?.id}>
                {subtitle?.name}
                {langIndex < (production?.languages?.length ?? 0) - 1 ? ', ' : ''}
              </Typography>
            ))}
          </div>
        )}
        {production?.subtitles?.length > 0 && (
          <div className={classes.subtitles}>
            <SpriteIcon icon="closed_caption" size={size + 6} className={classes.icon} />
            {production?.subtitles?.map((subtitle, langIndex) => (
              <Typography className={classes.language} size={size} color="secondary" key={subtitle?.id}>
                {showSlug ? subtitle?.slug : subtitle?.name}
                {langIndex < (production?.subtitles?.length ?? 0) - 1 ? '/' : ''}
              </Typography>
            ))}
          </div>
        )}
      </>
    )}
  </>
);

const CreditItem = ({ credit, trackingData, isLinkable = true, styles = { item: '' } }) => {
  const { permissions, navigate } = usePageContext();

  const creditLinkProps = useMemo(
    () =>
      navigate.getLinkProps({
        entityType: credit?.entityType,
        entity: credit?.entity,
        pro: permissions?.castingToolPermissions?.hasAccess,
        onlyLinkProps: true,
        rel: 'nofollow',
      }),
    [],
  );

  if (!credit) {
    return null;
  }

  return (
    <LinkButton
      styles={{ root: classes.creditLink }}
      variant="native"
      {...creditLinkProps}
      target="_blank"
      isLink
      trackingData={{
        ...trackingData,
        entityId: credit.entity?.id,
        entityName: credit.entity?.name,
        entityType: credit.entityType,
      }}
    >
      <>
        <Typography
          variant="span"
          color={isLinkable ? 'link' : 'primary'}
          className={classnames(classes.creditText, { [styles?.item]: !!styles?.item })}
        >
          <>
            <EntityName entityType={credit.entityType} entity={credit.entity} isRaw trackingData={trackingData} />
          </>
        </Typography>
      </>
    </LinkButton>
  );
};

const AboutDescription = ({ production }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const lang = i18n.language;

  const { data: descriptions, isFetched } = useQuery(queries.getProductionDescriptions({ id: production?.id }));

  if (isFetched && descriptions?.length <= 0) {
    return null;
  }

  const scrollTo = () => {
    scrollToSection(SECTION_KEYS.ABOUT);
  };

  return (
    <Typography variant="p" size={14} lang={lang}>
      <ParseHtml className={classes.description}>{descriptions?.[0].content?.slice(0, 280)}</ParseHtml>
      <Typography size={14} color="link" onClick={scrollTo} className={classes.viewMore}>
        {t(`${TP}.FN_VIEW_MORE`)}
        <SpriteIcon icon="expand_more" size={16} />
      </Typography>
    </Typography>
  );
};

const ProductionTitle = ({ production, className, isStickyHeader }) => {
  const works = useMemo(() => getProductionWorks(production?.productionWorks), [production]);
  const productionName = production?.name;
  const showCustomTitle = works?.length > 1 || (productionName && works?.[0]?.originalName !== productionName);

  return (
    <Typography variant={!isStickyHeader ? 'h1' : 'p'}>
      <WorkTitle
        title={showCustomTitle ? productionName || ' ' : ''}
        works={works}
        styles={{ containerRoot: classes.containerRoot, workTitle: classnames(classes.workTitle, className) }}
      />
    </Typography>
  );
};

const DirectorConductor = ({ directors, conductors, isSticky = false, styles }) => {
  const directorsList = directors?.data?.[0];
  const conductorsList = conductors?.data?.[0];

  if (!directorsList && !conductorsList) {
    return null;
  }

  return (
    <div className={classnames(classes.directorConductor, { [classes.stickyDc]: isSticky })}>
      <span className={classes.directorConductor__wrapper}>
        <Typography color="secondary" className={classes.directorConductor__wrapper_title}>
          D:
        </Typography>
        <CreditItem credit={directorsList} styles={styles} />
      </span>
      <span className={classes.directorConductor__wrapper}>
        <Typography color="secondary" className={classes.directorConductor__wrapper_title}>
          C:
        </Typography>
        <CreditItem credit={conductorsList} styles={styles} />
      </span>
    </div>
  );
};

const ProductionHeader = ({ production, trackingData, credits }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { directors, conductors, cast, crew, instrumentalists, producers } = credits;

  const restCastCrewIns = useMemo(() => {
    const allCredits = [...(crew || []), ...(cast || []), ...(instrumentalists || [])];

    return allCredits?.reduce((acc, credit) => {
      if (credit?.data?.length > 0) {
        acc.push(...credit?.data);
      }

      return acc;
    }, []);
  }, []);

  return (
    <div className={classes.headerSection}>
      <div className={classes.headerSection__left}>
        <div className={classes.headerSection__left_otherInfo}>
          <DateRangeAndPrefCount production={production} />
          <div className={classes.durationLandInfo}>
            <DurationAndLangInfo production={production} />
          </div>
        </div>
        <div className={classes.headerSection__left_tags}>
          <ProductionTags
            data={production}
            enableRentalsTag={false}
            enableWatchOptionsTag={false}
            enableTicketsTag={false}
            enableAvailabilityTags={false}
            enableProductionWorksTag={false}
            styles={{
              root: classes.productionTagList,
              productionTags: classes.productionTagsInner,
              workTag: classes.workTag,
              separatorTag: classes.separatorTag,
              stagingTypeTag: classes.stagingTypeTag,
            }}
            color="primary"
          />
        </div>
        <div className={classes.headerSection__left_title}>
          <ProductionTitle production={production} />
        </div>
        <div className={classes.headerSection__left_contributions}>
          <div className={classes.venue}>
            <CreditItem credit={producers?.data?.[0]} />
          </div>
          <DirectorConductor directors={directors} conductors={conductors} />
          <div className={classes.restCrew}>
            <Typography size={16} color="secondary" className={classes.restCrew__title}>
              {t(`${TP}.FN_STARRING`)}
              {': '}
            </Typography>
            <Overflow
              className={classes.restCrew__all}
              data={restCastCrewIns}
              renderRest={items => (
                <Typography color="link" size={16}>
                  &nbsp;{`+${items?.length}`}
                </Typography>
              )}
              renderItem={(item, index) => (
                <div key={index} className={classes.restCrew__item}>
                  <CreditItem credit={item} />
                  {restCastCrewIns?.length - 1 ? `, ` : ''}
                </div>
              )}
              maxCount={3}
            />
          </div>
        </div>
        <div>
          <AboutDescription production={production} />
        </div>
      </div>
      <div className={classes.headerSection__right}>
        <div className={classes.headerSection__right_actions}>
          <ProductionActions production={production} trackingData={trackingData} className={classes.actions} />
          <LivestreamAndVod
            production={production}
            styles={{ watchOptionsBlock: classes.watchOptionsBlock }}
            limit={2}
          />
        </div>
      </div>
    </div>
  );
};

const ProductionStubInfo = ({ entity: production, stickyElements, trackingData }) => {
  const { navigate, isHeaderVisible } = usePageContext();
  const { obRouteContext } = useAppContext();
  const [selectedPerformanceId, setSelectedPerformanceId] = useState(() => {
    if (production && obRouteContext?.linkProps?.filters?.[FILTER_TYPES.DATE]) {
      const { date_from: dateFrom } = obRouteContext.linkProps.filters[FILTER_TYPES.DATE];
      const performances = production?.performances?.filter(performance => performance.startDate === dateFrom);

      return performances?.[0]?.id;
    }

    return null;
  }, []);
  const credits = useMemo(() => getProductionCredits(production), [production]);
  const { directors, conductors } = credits;

  const { linkProps } = obRouteContext;
  const nextPerformance = production?.performances?.find(performance => new Date(performance.startDate) > new Date());

  const handleSelectedPerformance = useCallback(
    performanceId => {
      const performanceInfo = production?.performances?.find(performance => performance?.id === performanceId);

      const formattedDate = createDate(performanceInfo?.startDate, DATE_FORMATS.FULL_DATE)
        .locale('en')
        .format(DATE_FORMATS.FULL_DATE);

      const performanceLinkProps = navigate.getLinkProps({
        ...linkProps,
        filters: {
          [FILTER_TYPES.DATE]: {
            date_from: formattedDate,
            date_to: formattedDate,
          },
        },
        skipAutoTab: true,
      });

      setSelectedPerformanceId(performanceId);
      navigate.to(performanceLinkProps);
    },
    [production, navigate, setSelectedPerformanceId, linkProps],
  );

  const stickyContent = useMemo(
    () => (
      <div className={classes.stickyHeader}>
        <div className={classes.stickyHeader__wrapper}>
          <div className={classes.stickyHeader__wrapper_block}>
            <div className={classes.left}>
              <DateAndVenue
                production={production}
                styles={{ singleDateRoot: classes.stickyDateRoot }}
                selectedPerformanceId={nextPerformance?.id}
              />
              <div className={classes.titleContent}>
                <ProductionTitle production={production} className={classes.stickyTitle} isStickyHeader />
                <div className={classes.stickyCrew}>
                  <DirectorConductor
                    directors={directors}
                    conductors={conductors}
                    isSticky={!isHeaderVisible}
                    styles={{ item: classes.creditItem }}
                  />
                </div>
              </div>
            </div>
            <div className={classes.right}>
              <ProductionActions
                production={production}
                trackingData={trackingData}
                className={classes.stickyActions}
                isSticky={!isHeaderVisible}
              />
            </div>
          </div>
          <div>
            {stickyElements[SECTION_REFS.NAVIGATION_TABS] && (
              <NavigationTabs entity={production} className={classes.stickyNavTabs} />
            )}
          </div>
        </div>
      </div>
    ),
    [],
  );

  return (
    <StickyContainer isStickyHeader top={90} stickyContent={stickyContent}>
      <ProductionHeader production={production} credits={credits} trackingData={trackingData} />
      {production?.rentals?.length > 0 && (
        <div className={classes.rentalBanner}>
          <RentalBanner entity={production} />
        </div>
      )}
      <div className={classes.upcomingCalendarView}>
        <div className={classes.upcomingCalendarView__nextPerformance}>
          <DateAndVenue
            production={production}
            styles={{ singleDateRoot: classes.singleDateRoot }}
            selectedPerformanceId={nextPerformance?.id}
          />
        </div>
        <div className={classes.upcomingCalendarView__calendar}>
          <ProductionCalendarDates
            production={production}
            selectedPerformanceId={selectedPerformanceId}
            setSelectedPerformanceId={handleSelectedPerformance}
          />
        </div>
      </div>
    </StickyContainer>
  );
};

export default ProductionStubInfo;
